import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { ShieldCheck, ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1599880917812-35b7cde34597?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxjb21tZXJjaWFsJTIwb2ZmaWNlJTIwY2xlYW5pbmclMjBoeWdpZW5lJTIwbW9kZXJufGVufDB8fHxibHVlfDE3ODkyMDUyODl8MA&ixlib=rb-4.1.0&q=85&w=1200&auto=format&fit=crop";

const LINES = ["SPOTLESS SPACES.", "GOLD STANDARD", "SERVICE."];

const STATS = [
  { value: "07", label: "Specialist Sectors" },
  { value: "99.99%", label: "Pathogen Neutralisation" },
  { value: "24/7", label: "Emergency Dispatch" },
  { value: "100%", label: "Eco-Conscious Formulas" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 140]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.25]);

  const rotateX = useSpring(0, { stiffness: 120, damping: 16 });
  const rotateY = useSpring(0, { stiffness: 120, damping: 16 });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    rotateY.set(((e.clientX - r.left) / r.width - 0.5) * 14);
    rotateX.set(-((e.clientY - r.top) / r.height - 0.5) * 14);
  };
  const resetTilt = () => {
    rotateX.set(0);
    rotateY.set(0);
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen grain overflow-hidden flex flex-col justify-center pt-28 pb-16"
    >
      <div className="absolute inset-0 spotlight" />
      <div className="absolute -top-32 -right-32 w-[34rem] h-[34rem] rounded-full bg-gold-500/10 blur-[120px]" />
      <div className="absolute bottom-0 -left-40 w-[30rem] h-[30rem] rounded-full bg-navy-600/30 blur-[100px]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-[1.25fr_1fr] gap-14 items-center">
        <motion.div style={{ opacity: fade }}>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="eyebrow mb-6"
            data-testid="hero-eyebrow"
          >
            Gold Link — Commercial & Residential Cleaning
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display font-black text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem] leading-[0.98] tracking-tight"
          >
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={
                    "block " + (i === 1 ? "text-gold-500" : "text-ice-50")
                  }
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{
                    delay: 0.3 + i * 0.14,
                    duration: 0.9,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-ice-200/70"
            data-testid="hero-subcopy"
          >
            From corporate towers to care homes, concerts to car washes — Gold
            Link delivers hospital-grade hygiene across the West Midlands, on
            schedule, without disruption.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-quote"
              onClick={() => scrollToId("quote")}
              className="group inline-flex items-center gap-2 bg-gold-500 text-navy-900 font-heading font-bold text-sm uppercase tracking-widest px-7 py-4 rounded-full transition-all duration-300 hover:bg-gold-400 hover:gap-3.5"
            >
              Request Custom Quote
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button
              data-testid="hero-cta-sectors"
              onClick={() => scrollToId("services")}
              className="inline-flex items-center gap-2 border border-ice-200/25 text-ice-50 font-heading font-semibold text-sm uppercase tracking-widest px-7 py-4 rounded-full hover:border-gold-500 hover:text-gold-400 transition-colors duration-300"
            >
              Explore 7 Sectors <ArrowDown className="w-4 h-4" />
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="hidden lg:block"
          style={{ perspective: 1000 }}
        >
          <motion.div
            data-testid="hero-tilt-card"
            onMouseMove={onTilt}
            onMouseLeave={resetTilt}
            style={{ rotateX, rotateY, y: imgY, transformStyle: "preserve-3d" }}
            className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)]"
          >
            <img
              src={HERO_IMG}
              alt="Gold Link professional cleaning operative in a modern office"
              className="w-full h-[30rem] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-transparent to-transparent" />
            <div
              className="absolute bottom-5 left-5 right-5 flex items-center gap-3 bg-navy-900/70 backdrop-blur-md border border-white/10 rounded-xl px-4 py-3"
              style={{ transform: "translateZ(40px)" }}
            >
              <span className="w-10 h-10 rounded-full bg-gold-500/15 grid place-items-center">
                <ShieldCheck className="w-5 h-5 text-gold-400" />
              </span>
              <div>
                <p className="font-heading font-bold text-sm text-ice-50">
                  Hospital-Grade Certified
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ice-200/60">
                  ISO 9001 Protocol Active
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="relative max-w-7xl mx-auto px-5 sm:px-8 w-full mt-16"
      >
        <div
          data-testid="hero-stats-bar"
          className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10 rounded-2xl overflow-hidden backdrop-blur-md"
        >
          {STATS.map((s) => (
            <div
              key={s.label}
              className="bg-navy-900/60 px-6 py-5 hover:bg-navy-800/60 transition-colors duration-300"
            >
              <p className="font-display font-extrabold text-2xl sm:text-3xl text-gold-400">
                {s.value}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice-200/60 mt-1">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
